import { useCallback, useRef, useState } from 'react';
import type React from 'react';
import type { EvidenceItem, EvidenceType } from '../../../../types/record';

export type EvidenceIdFactory = () => string;

export const inferEvidenceType = (file: File): EvidenceType => {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();

  if (type.startsWith('image/') || name.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
    return 'image';
  }
  if (type.startsWith('audio/') || name.match(/\.(mp3|wav|ogg|m4a)$/)) {
    return 'audio';
  }
  if (type.includes('pdf') || name.endsWith('.pdf')) {
    return 'document';
  }
  if (type.includes('email') || name.match(/\.(eml|msg)$/)) {
    return 'email';
  }
  if (name.match(/\.(txt|transcript)$/)) {
    return 'transcript';
  }
  return 'document';
};

export const createDraftEvidenceItems = (
  files: File[],
  uploadedAt: string = new Date().toISOString(),
  idFactory: EvidenceIdFactory = defaultEvidenceId
): EvidenceItem[] =>
  files.map((file) => ({
    id: idFactory(),
    filename: file.name,
    type: inferEvidenceType(file),
    size: file.size,
    uploadedAt,
  }));

export function useRecordEvidence(initialEvidence: EvidenceItem[] = []) {
  const [evidence, setEvidence] = useState<EvidenceItem[]>(initialEvidence);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;
    setEvidence((prev) => [...prev, ...createDraftEvidenceItems(files)]);
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    addFiles(Array.from(event.dataTransfer.files));
  }, [addFiles]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    addFiles(Array.from(files));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [addFiles]);

  return {
    evidence,
    isDragging,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileSelect,
  };
}

function defaultEvidenceId(): string {
  return `evidence-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
